// @ts-strict
import { DocumentType, modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { AssetType } from "../../Configurations/AssetTypeConfiguration/AssetTypeConfiguration.js";
import { isDuplicateKeyError } from "../../utils/mongoErrorCodes.js";
import { verdiIdRegex } from "../../utils/shortCodes/shortCodesLookup.js";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { AssetIdentityFieldsWithDefaults, AssetIdentitySchemaTypes } from "../types/assetIdentity.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "AssetIdentity" as const;
export const loraEuiRegex = /^eui-[0-9a-f]{16}$/;

export class VerdiIdCollisionError extends Error {
    constructor(verdiId: string) {
        super(
            `verdiId collision on "${verdiId}": two concurrent saves generated the same ID. ` +
                "Retry with a newly generated verdiId.",
        );
        this.name = "VerdiIdCollisionError";
    }
}
export { AssetType };

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
        collection: "assetIdentities",
        timestamps: true,
    },
})
@post<AssetIdentity>(
    "save",
    function (
        this: void,
        error: mongoose.mongo.MongoServerError,
        doc: DocumentType<AssetIdentity>,
        next: mongoose.CallbackWithoutResultAndOptionalError,
    ) {
        if (isDuplicateKeyError(error) && error.keyPattern?.["verdiId"]) {
            console.info(`VerdiIdCollisionError: collision on "${doc.verdiId}"`);
            next(new VerdiIdCollisionError(doc.verdiId));
        } else {
            next(error);
        }
    },
)
@post<SchemaBase<AssetIdentity>>(...addSchemaKeyHook(SCHEMA_KEY))
export class AssetIdentity extends SchemaBase<AssetIdentity, AssetIdentityFieldsWithDefaults> {
    static readonly schemaKey: string = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey: string = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * Which field on the asset's home-collection document holds its verdiId. Always "verdiId" today; kept as an
     * explicit field so joins/lookups don't hard-code the field name.
     */
    @validate({ zodValidator: z.literal("verdiId"), responseOnly: true })
    @prop({ type: () => String, required: true, enum: ["verdiId"], default: "verdiId" })
    public verdiIdField = "verdiId" as const;

    /**
     * What collection is this asset in in mongo? For doing joins.
     */
    @validate({ zodValidator: z.string().min(1), responseOnly: true })
    @prop({ type: () => String, required: true })
    public assetCollection!: string;

    /**
     * The Verdi short code, which will eventually supplant object ID as the standard unique identifier for assets
     */
    @validate({
        zodValidator: z.string().regex(verdiIdRegex),
        responseOnly: true,
    })
    @prop({
        type: () => String,
        required: true,
        unique: true,
        match: verdiIdRegex,
    })
    public verdiId!: string;

    /**
     * If this asset has a Lora EUI, then it should be registered here to avoid registering duplicate EUIs.
     */
    @validate({
        zodValidator: z.string().regex(loraEuiRegex).optional(),
        responseOnly: true,
    })
    @prop({ type: () => String, required: false, unique: true, sparse: true, match: loraEuiRegex })
    public loraEui?: string;

    @validate({ zodValidator: z.enum(Object.values(AssetType) as [string, ...string[]]), responseOnly: true })
    @prop({ type: () => String, required: true, enum: Object.values(AssetType) })
    public assetType!: AssetType;

    static getters = {} as const satisfies Record<string, (self: AssetIdentity) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<AssetIdentitySchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<AssetIdentity, AssetIdentityFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

AssetIdentity.dtoValidators = getZodSchemaFromClass(
    AssetIdentity,
) as unknown as DtoValidatorTypes<AssetIdentitySchemaTypes>;
